import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import { Button, Divider } from '@mui/material';
import { Title } from 'shared/components/abstract/interactables';
import { getBuildingsByNas } from 'shared/api/buildings';
import { useNotification, useRequest } from 'shared/hooks';
import { buildingEntity, nasEntity } from 'shared/entityDetails';
import { VARIANTS } from 'shared/constants';
import * as routes from 'shared/routes/networking';
import { getBuildingToolsPath } from 'shared/routes/networking';
import CardDisplay from 'features/networking/ui/CardDisplay';
import CardSelector, { entityEnum } from 'features/networking/ui/CardSelector';
import BuildingCard from 'features/networking/building/BuildingCard';

const paramKey = 'building_id';

export default function BuildingTools() {
  const { request } = useRequest();
  const { search } = useLocation();
  const { push: pushHistory } = useHistory();
  const { pushInfo } = useNotification();

  const [clearable, setClearable] = useState(false);
  const [entityType, setEntityType] = useState(entityEnum.BUILDING);

  useEffect(() => {
    const { length } = new URLSearchParams(search).getAll(paramKey);
    if (clearable && length === 0) {
      setClearable(false);
      return;
    }
    if (!clearable && length > 0) {
      setClearable(true);
    }
  }, [clearable, search]);

  const handleClear = () => {
    pushHistory(getBuildingToolsPath([]));
  };

  function addBuilding(building) {
    const current = new URLSearchParams(search).getAll(paramKey);
    if (current.find((curr) => curr === building.id.toString())) {
      pushInfo('Building is already added');
    } else {
      pushHistory(getBuildingToolsPath([building.id, ...current]));
    }
  }

  async function addNas(nas) {
    const result = await request(getBuildingsByNas(nas.id));
    if (!result) return;
    if (!result.total) {
      pushInfo(`No buildings are associated to "${nas.nas_name}"`);
    } else {
      const distinctIds = new Set([
        ...result.rows.map((r) => r.id.toString()),
        ...new URLSearchParams(search).getAll(paramKey),
      ]);
      pushHistory(getBuildingToolsPath(Array.from(distinctIds)));
      pushInfo(`Added ${result.rows.length} buildings`);
    }
  }

  const entityMap = {
    [entityEnum.BUILDING]: {
      ...buildingEntity,
      add: addBuilding,
    },
    [entityEnum.NAS]: {
      ...nasEntity,
      baseSearch: 'expand=nas_profiles',
      add: addNas,
    },
  };

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <Title
          title="Building Tools"
          titleLink={routes.buildingToolsPath}
        />
      </Grid>
      <Grid container size={12} rowSpacing={1} sx={{ alignItems: 'end' }}>
        <Grid size={{
          xs: 12,
          md: 9,
        }}
        >
          <CardSelector
            entityType={entityType}
            setEntityType={setEntityType}
            entityMap={entityMap}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
          sx={(theme) => ({
            display: 'flex',
            [theme.breakpoints.up('md')]: {
              justifyContent: 'end',
            },
          })}
        >
          <Button
            sx={{
              mb: '10px',
            }}
            disabled={!clearable}
            variant={VARIANTS.outlined}
            onClick={handleClear}
          >
            Clear All
          </Button>
        </Grid>
      </Grid>
      <Grid size={12}>
        <Divider flexItem sx={{ color: 'red' }} />
      </Grid>
      <CardDisplay
        cardComponent={BuildingCard}
        urlParamKey={paramKey}
      />

    </Grid>
  );
}
